/* eslint-disable @typescript-eslint/no-explicit-any */
import { BasicFile, DatePickerAPI, DynamicFieldsAPI, FormAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ScheduleWaitingListData } from '../../app/types';
import { AdditionalInformation, AdditionalInformationValues, AppointmentEstablishment, CurrentPatientSelected, DateRange, DocumentUploadScheduleRuleResponse, EstablishmentGroupLocalizationModel, HealthInsuranceCardProps, PriorLocation, ProcedureModel, PseudonymWaitingListData, QuizMetadata, Suggestions } from './pseudonym-interfaces';
import PseudonymSuggestionListModel from './pseudonym-suggestion/pseudonym-suggestion-list-model';
import { CheckupSuggestion, CheckupSuggestionGroups, CheckupSuggestionTest, ScheduleItem, SuggestionItems } from '../../interfaces/schedules-suggestions';

export default class PseudonymAppointmentModel {

  // General
  static integratedScheduleSequence: number;
  static isQuizAvailable: boolean;
  static hasMetadataQuiz: boolean;
  static referenceCompanyCode: number;

  // Step 1 - Patient Informations
  static stepOne = {
    appointmentEstablishment: {
      cdEstablishment: 0 as number,
      dsEstablishmentName: '' as string
    } as AppointmentEstablishment,
    currentPatientSelected: {} as CurrentPatientSelected,
    healthInsurance: {} as HealthInsuranceCardProps,
    priorLocation: {} as PriorLocation,
    releasedPlan: true as boolean,
  };

  // Step 2 - Procedures
  static stepTwo = {
    clearFields: () => { },
    clearProfessionalField: () => { },
    hasCheckup: false as boolean,
    hasExam: false as boolean,
    hasProcedureChange: false as boolean,
    isOnlyConsultation: false as boolean,
    procedures: [] as Array<ProcedureModel>,
    stepTwoOk: true as boolean,
  };

  // Step 3 - Additional Information
  static stepThree = {
    additionalInformation: {} as AdditionalInformation,
    additionalInformationValues: {} as AdditionalInformationValues,
    loadAdditionalInformation: () => { },
    saveAdditionalInformation: () => { },
  };

  // Step 4 - Quiz
  static stepFour = {
    populateQuizTab: (): Promise<void> => { return Promise.resolve() },
    quizDynamicFieldsApi: {} as DynamicFieldsAPI,
    quizDynamicMetada: {} as QuizMetadata,
    quizFormApi: {} as FormAPI,
  };

  // Step 5 - Location
  static stepFive = {
    loadLocation: () => {},
    location: {} as EstablishmentGroupLocalizationModel,
  };

  // Step 6 - Schedule Period
  static stepSix = {
    allowSelectDesiredDate: false as boolean,
    availableDays: [],
    datePickerScheduleRange: {} as DatePickerAPI,
    desiredDateCustomMessage: '',
    panelNotificationSchedulePeriodApi: {} as PanelAPI,
    selectedDateRange: {} as DateRange,
    verifyAvailableCalendarDays: () => { },
  };

  // Step 7 - Suggestions
  static stepSeven = {
    availableDays: [],
    datePickerScheduleRange: {} as DatePickerAPI,
    hasSelected: false as boolean,
    hasSelectedEstablishment: false as boolean,
    isLastSuggestionDenied: false as boolean,
    isMessageDefault: true as boolean,
    isSelected: false as boolean,
    multiSelected: [] as Array<any>,
    panelLoaderConfirmationApi: {} as PanelAPI,
    populateSuggestionTab: () => { },
    refreshTrailChoosenItems: () => { },
    selectedDateEstablishment: '' as string,
    suggestionListModel: {} as PseudonymSuggestionListModel,
    suggestions: {} as Suggestions,
    suggestionSelected: {} as Array<SuggestionItems>,
    unSelectAllCards: () => { },
    verifyIfCanHandleConfirmSuggestion: () => { },
  }

  // Step 7.1 - another period
  static readonly stepAnotherPeriod = {
    anotherPeriodRange: {} as DateRange,
    availableDays: [],
    datePickerScheduleRange: {} as DatePickerAPI,
    findAnotherPhysician: false as boolean,
    hasValues: false as boolean,
    isAnotherPeriodSelected: false as boolean,
    isLastSuggestionDenied: false as boolean,
    isPrevStep: false as boolean,
    panelNotificationSchedulePeriodApi: {} as PanelAPI,
  }

  // Step 8 - Summary
  static stepEight = {
    hasCheckup: false,
    hasSelectedConsultations: false,
    hasSelectedExams: false,
    hasSelectedSevices: false,
    hasWaitingList: false,
    panelLoaderConfirmationApi: {} as PanelAPI,
    paymentConfiguration: '',
    populateSummaryCardsWithSelectedItems: () => { }
  }

  // Step 7.5 - Documents
  static stepDocumentsUpload = {
    documentsToUpload: [] as DocumentUploadScheduleRuleResponse[],
    hasFilesToUpload: false as boolean,
    memoryFiles: Object as unknown as Map<string, Blob>,
    populateFileUploaderItems: (): Promise<void> => { return Promise.resolve() },
    uploadedFiles: [] as Array<BasicFile>,
  };

  // Waiting List
  static waitingList = {
    pseudonymWaitingListData: {} as PseudonymWaitingListData,
    scheduleWaitingListDataArray: [] as Array<ScheduleWaitingListData>,
    waitingListScheduled: 0 as number,
  };

  public static readonly findAnotherPhysician = {
    emptyEstablishmentGroups: false as boolean,
    emptySuggestions: [] as Array<ScheduleItem>,
  }

  // Step Checkup Suggestions
  static stepCheckupSuggestions = {
    checkupRequisitionParameters: {} as CheckupSuggestionTest,
    checkupSuggestionGroups: {} as CheckupSuggestionGroups,
    checkupType: '',
    populateCheckupSuggestion: () => { },
    scheduleItem: {} as ScheduleItem[],
    selectedSuggestion: {} as CheckupSuggestion,
  };

  static readonly establishmentVisibilityControl = new Map();

  static clear(): void {
    // Default Values
    const CURRENT_APPOINTMENT_ESTABLISHMENT_DEFAULT_VALUE = { cdEstablishment: 0, dsEstablishmentName: '' } as AppointmentEstablishment;
    const CURRENT_PRIOR_LOCATION_SELECTED_DEFAULT_VALUE = { description: '', isVisible: false, sequenceNumber: 0 } as PriorLocation;
    const CURRENT_PATIENT_SELECTED_DEFAULT_VALUE = { id: 0, name: '' } as CurrentPatientSelected;
    const HEALTH_INSURANCE_DEFAULT_VALUE = {
      cardId: '',
      categoryDesc: '',
      categoryId: '',
      healthCardNumber: '',
      healthInsuranceDesc: '',
      healthInsuranceId: 0,
      isNewItemButton: false,
      isPrivate: false,
      planDesc: '',
      planId: '',
      walletExpiryDate: '',
    } as HealthInsuranceCardProps;

    this.integratedScheduleSequence = 0;
    this.isQuizAvailable = false;
    this.clearStepOne(CURRENT_PATIENT_SELECTED_DEFAULT_VALUE, HEALTH_INSURANCE_DEFAULT_VALUE, CURRENT_PRIOR_LOCATION_SELECTED_DEFAULT_VALUE, CURRENT_APPOINTMENT_ESTABLISHMENT_DEFAULT_VALUE);
    this.clearStepTwo();
    this.clearStepThree();
    this.clearStepFour();
    this.clearStepFive();
    this.clearStepSix();
    this.clearStepSeven();
    this.clearStepEight();
    this.clearDocumentsUploadStep();
    this.clearCheckupSuggestion();
    this.clearWaitingList(CURRENT_PATIENT_SELECTED_DEFAULT_VALUE, HEALTH_INSURANCE_DEFAULT_VALUE);
  }

  // Step 1 - Patient Informations
  private static clearStepOne(currentPatientSelected: CurrentPatientSelected, healthInsurance: HealthInsuranceCardProps, priorLocation: PriorLocation, appointmentEstablishment: AppointmentEstablishment): void {
    this.stepOne = {
      appointmentEstablishment: appointmentEstablishment,
      currentPatientSelected: currentPatientSelected,
      healthInsurance: healthInsurance,
      priorLocation: priorLocation,
      releasedPlan: true as boolean,
    };
  }

  // Step 2 - Procedures
  private static clearStepTwo(): void {
    this.stepTwo = {
      clearFields: () => { },
      clearProfessionalField: () => { },
      hasCheckup: false,
      hasExam: false,
      hasProcedureChange: false,
      isOnlyConsultation: false,
      procedures: [],
      stepTwoOk: true as boolean,
    };
  }

  // Step 3 - Additional Information
  private static clearStepThree(): void {
    const ADDITIONAL_INFORMATION_DEFAULT_VALUE = { heightInCentimeters: 0, saveAdditionalInfo: false, weight: 0 } as AdditionalInformation;

    this.stepThree = {
      additionalInformation: ADDITIONAL_INFORMATION_DEFAULT_VALUE,
      additionalInformationValues: {
        newValue: ADDITIONAL_INFORMATION_DEFAULT_VALUE,
        oldValue: ADDITIONAL_INFORMATION_DEFAULT_VALUE,
      },
      loadAdditionalInformation: () => { },
      saveAdditionalInformation: () => { },
    };
  }

  // Step 4 - Quiz
  private static clearStepFour(): void {
    this.stepFour = {
      populateQuizTab: (): Promise<void> => { return Promise.resolve() },
      quizDynamicFieldsApi: {} as DynamicFieldsAPI,
      quizDynamicMetada: {} as QuizMetadata,
      quizFormApi: {} as unknown as FormAPI,
    };
  }

  // Step 5 - Location
  private static clearStepFive(): void {
    this.stepFive = {
      loadLocation: () => {},
      location: { dsGrupoEstab: '', nrSequencia: 0 },
    };
  }

  // Step 6 - Schedule Period
  private static clearStepSix(): void {
    this.stepSix = {
      allowSelectDesiredDate: false,
      availableDays: [],
      datePickerScheduleRange: {} as DatePickerAPI,
      desiredDateCustomMessage: '',
      panelNotificationSchedulePeriodApi: {} as PanelAPI,
      selectedDateRange: { end: '', start: '' },
      verifyAvailableCalendarDays: () => { },
    };
  }

  // Step 7 - Suggestions
  public static clearStepSeven(): void {
    this.stepSeven = {
      availableDays: [],
      datePickerScheduleRange: {} as DatePickerAPI,
      hasSelected: false,
      hasSelectedEstablishment: false,
      isLastSuggestionDenied: false,
      isMessageDefault: true as boolean,
      isSelected: false,
      multiSelected: [],
      panelLoaderConfirmationApi: {} as PanelAPI,
      populateSuggestionTab: () => { },
      refreshTrailChoosenItems: () => { },
      selectedDateEstablishment: '' as string,
      suggestionListModel: {} as PseudonymSuggestionListModel,
      suggestions: {
        blockedSuggestionList: [],
        isEnabledToScheduleWaitingList: false,
        isShowNotificationWaitingList: false,
        suggestionGroups: [],
        suggestionList: [],
      },
      suggestionSelected: {} as Array<SuggestionItems>,
      unSelectAllCards: () => {},
      verifyIfCanHandleConfirmSuggestion: () => {},
    };
  }

  // Step 8 - Summary
  private static clearStepEight(): void {
    this.stepEight = {
      hasCheckup: false,
      hasSelectedConsultations: false,
      hasSelectedExams: false,
      hasSelectedSevices: false,
      hasWaitingList: false,
      panelLoaderConfirmationApi: {} as PanelAPI,
      paymentConfiguration: '',
      populateSummaryCardsWithSelectedItems: () => {}
    };
  }

  // Waiting List
  private static clearWaitingList(currentPatientSelected: CurrentPatientSelected, healthInsurance: HealthInsuranceCardProps): void {
    this.waitingList = {
      pseudonymWaitingListData: {
        availablePeriod: [],
        dependencies: {
          calendarFinalDateInitialRange: '',
          currentPatientSelected: currentPatientSelected,
          healthInsuranceData: healthInsurance,
        },
        nrSeqAgeint: 0,
        nrSeqAgeintItem: '',
        procedureList: [],
        urgencyReason: { dsReason: '', nrSequence: 0 }
      },
      scheduleWaitingListDataArray: [],
      waitingListScheduled: 0 as number,
    };
  }

  private static clearDocumentsUploadStep(): void {
    this.stepDocumentsUpload = {
      documentsToUpload: [] as DocumentUploadScheduleRuleResponse[],
      hasFilesToUpload: false as boolean,
      memoryFiles: Object as unknown as Map<string, Blob>,
      populateFileUploaderItems: (): Promise<void> => { return Promise.resolve() },
      uploadedFiles: [] as Array<BasicFile>,
    }
  }

  // Step Checkup Suggestion
  private static clearCheckupSuggestion(): void {
    this.stepCheckupSuggestions = {
      checkupRequisitionParameters: {} as CheckupSuggestionTest,
      checkupSuggestionGroups: {} as CheckupSuggestionGroups,
      checkupType: '',
      populateCheckupSuggestion: () => { },
      scheduleItem: [] as ScheduleItem[],
      selectedSuggestion: {} as CheckupSuggestion,
    }
  }
}
